const { logLevels, rewindStack, interpretParams } = require('./helpers');
const bridgeLogger = require('./strategies/bridgeLogger');
const consoleLogger = require('./strategies/consoleLogger');
const memoryLogger = require('./strategies/memoryLogger');
const sentryLogger = require('./strategies/sentryLogger');

/** @enum {number} */
const innerOutputType = Object.freeze({
    CONSOLE: 1,
    MEMORY: 2, // for debugging and tests.
    //FILE: 4, // for on-device
    // Use bridge pattern (https://en.wikipedia.org/wiki/Bridge_pattern) for device specific logging
    // such as logcat on Android
    BRIDGE: 8,
    SENTRY: 16,
    GCLOUD: 32
});

// Use a class to allow getters for logging deprecated properties.
class Logging {
    constructor() {
        //wire up pass-through methods
        //Memory Logging
        this.getMemoryLog = memoryLogger.getMemoryLog;
        this.clearMemoryLog = memoryLogger.clearMemoryLog;

        //Bridge Logging
        this.setBridgeLogger = bridgeLogger.setBridgeLogger;

        this.loggers = [consoleLogger];
        this.sendToOutput = (message, userContext, logLevel) => {
            this.loggers.forEach(logger => {
                if (logger.init && !logger.hasBeenInitialized) {
                    logger.init(this);
                    logger.hasBeenInitialized = true;
                }
                logger.output(message, userContext, logLevel, this.release);
            });
        };
    }

    /**
     * Supported Log Levels
     * @readonly
     * @enum {number}
     */
    get level() {
        return logLevels;
    }
    /** @enum {number} */
    get outputType() {
        return innerOutputType;
    }

    /**
     *
     * @param {*} message
     * @param {string[]} [optionalParams] If provided, will be concatenated to the message
     * @param {object} [userContext] Context to include in structured logs
     * @param {string} userContext.tenantId
     * @param {object} [userContext.activeTenant]
     * @param {string} userContext.appId
     * @param {object} [userContext.appContext]
     * @param {string} userContext.userId
     * @param {string} userContext.userName
     */
    debug(...allParams) {
        if (this.currentLogLevel < logLevels.DEBUG) return;
        let params = interpretParams(allParams);
        this.sendToOutput(params.message, params.userContext, logLevels.DEBUG);
    }

    /**
     *
     * @param {*} message
     * @param {string[]} [optionalParams] If provided, will be concatenated to the message
     * @param {object} [userContext] Context to include in structured logs
     * @param {string} userContext.tenantId
     * @param {object} [userContext.activeTenant]
     * @param {string} userContext.appId
     * @param {object} [userContext.appContext]
     * @param {string} userContext.userId
     * @param {string} userContext.userName
     */
    info(...allParams) {
        if (this.currentLogLevel < logLevels.INFO) return;
        let params = interpretParams(allParams);
        this.sendToOutput(params.message, params.userContext, logLevels.INFO);
    }

    /**
     * logs passed in message (as is) at error level
     * @param {Error|string} error Error object or warning message
     * @param {string[]} [optionalParameters] If provided, will be concatenated to the message
     * @param {object} [userContext] Context to include in structured logs
     * @param {string} userContext.tenantId
     * @param {object} [userContext.activeTenant]
     * @param {string} userContext.appId
     * @param {object} [userContext.appContext]
     * @param {string} userContext.userId
     * @param {string} userContext.userName
     */
    warn(...allParams) {
        if (this.currentLogLevel < logLevels.WARN) return;
        let params = interpretParams(allParams);
        if (params.message[0] instanceof UserError || params.message[0]?._isUserError) {
            this.info(allParams[0].message, ...allParams.slice(1));
            return;
        }
        if (params.message[0] && typeof params.message[0] === 'string') {
            params.message[0] = params.message[0].replace('Error: ', '');
        }
        this.sendToOutput(params.message, params.userContext, logLevels.WARN);
    }

    /**
     * Logs error with stack info at error level
     * @param {Error|string} error Error object or error message
     * @param {string[]} [optionalParams] If provided, will be concatenated to the message
     * @param {object} [userContext] Context to include in structured logs
     * @param {string} userContext.tenantId
     * @param {object} [userContext.activeTenant]
     * @param {string} userContext.appId
     * @param {object} [userContext.appContext]
     * @param {string} userContext.userId
     * @param {string} userContext.userName
     */
    error(...allParams) {
        let params = interpretParams(allParams);
        if (params.message[0] instanceof UserError || params.message[0]?._isUserError) {
            this.info(allParams[0].message, ...allParams.slice(1));
            return;
        }
        if (!(params.message[0] instanceof Error)) {
            if (params.message.length > 1 || typeof params.message[0] !== 'string') {
                let error = new Error('');
                rewindStack(error, 1); //Hide this method from the stack trace
                params.message.push(error.stack.replace(/^Error[\n|: ]/, '')); //strip "Error" from stack, push the stack on the messages
            } else {
                let error = new Error(params.message[0]);
                rewindStack(error, 1); //Hide this method from the stack trace
                params.message = [error];
            }
        }
        this.sendToOutput(params.message, params.userContext, logLevels.ERROR);
    }

    setMinLogLevel(logLevel) {
        switch (logLevel) {
            case logLevels.ERROR:
            case logLevels.WARN:
            case logLevels.INFO:
            case logLevels.DEBUG:
                this.currentLogLevel = logLevel;
                return;

            default:
                throw new Error(`${logLevel} is not a valid value for log level.`);
        }
    }

    setOutputType(outputType) {
        if (outputType < 1 || outputType > 63) {
            throw new Error(`${outputType} is not a valid value for output type.`);
        }
        if (!(outputType & innerOutputType.SENTRY)) sentryLogger.stop();
        this.loggers = [];
        if (outputType & innerOutputType.CONSOLE) {
            this.loggers.push(consoleLogger);
        }
        if (outputType & innerOutputType.MEMORY) {
            this.loggers.push(memoryLogger);
        }
        if (outputType & innerOutputType.FILE) {
            console.error(
                Error(
                    "Please use `const fileLogger = require('@sstdev/lib_logging/strategies/fileLoggerBridge');` followed by `logging.setBridgeLogger(fileLogger);`"
                )
            );
        }
        if (outputType & innerOutputType.BRIDGE) {
            this.loggers.push(bridgeLogger);
        }
        if (outputType & innerOutputType.SENTRY) {
            if (this.environment === 'prod') {
                this.loggers.push(sentryLogger);
            }
        }
    }
    addLogger(logger) {
        this.loggers.push(logger);
    }
    setLoggers(loggers) {
        this.loggers = loggers;
    }
    setIfShouldRemoveCarriageReturns() {
        this.error(Error('"setIfShouldRemoveCarriageReturns" is deprecated'));
    }

    getFileLogger() {
        this.error(
            Error(
                "Please use `const fileLogger = require('@sstdev/lib_logging/strategies/fileLoggerBridge');` followed by `logging.setBridgeLogger(fileLogger);`"
            )
        );
    }

    setRelease(release) {
        this.release = release;
    }

    setEnvironment(environment) {
        this.environment = environment;
        if (environment !== 'prod') {
            sentryLogger.stop();
            this.loggers = this.loggers.filter(logger => logger !== sentryLogger);
        }
    }

    setUserId(userId) {
        this.userId = userId;
    }

    setTenantId(tenantId) {
        this.tenantId = tenantId;
    }

    setUseCaseTitle(useCaseTitle) {
        this.useCaseTitle = useCaseTitle;
    }
    /**
     *
     * @param {number} sampleRate 0 through 1
     */
    setSampleRate(sampleRate = 1) {
        // https://docs.sentry.io/platforms/javascript/configuration/sampling/
        this.sampleRate = sampleRate;
    }

    setIgnoredErrors(ignoredErrors) {
        this.ignoredErrors = ignoredErrors;
    }
}
/**
 * @class
 * @classdesc Descendant of Error, which will override ERROR and WARN level logging,
 * logging at INFO level instead
 */
class UserError extends Error {
    constructor(message) {
        super(message);
        this._isUserError = true;
    }
}

module.exports = new Logging();
module.exports.UserError = UserError;
